import type {
  CityAllyModelId,
  CityBuildingFunction,
} from "./phase-three-types";

export interface CityAllyDefinition {
  modelId: CityAllyModelId;
  presentationId: string;
  maxHealth: number;
  armor: number;
  damage: number;
  attackIntervalMs: number;
  attackRange: number;
  moveSpeed: number;
  alertRadius: number;
  activityRadius: number;
}

export interface CityBuildingDefinition {
  id: string;
  name: string;
  presentationId: string;
  function: CityBuildingFunction;
  offsetX: number;
  offsetY: number;
  radius: number;
  maxHealth: number;
  armor: number;
  production?: {
    allyModelId: CityAllyModelId;
    intervalMs: number;
    batchSize: number;
  };
}

export const CITY_GATE_LINK_RADIUS = 330;
export const CITY_ALLY_RALLY_RADIUS = 54;
export const CITY_ENEMY_AGGRO_CONFIG = {
  buildingRadius: 1_120,
  retaliationRadius: 1_240,
  retaliationMemoryMs: 3_000,
} as const;

export const CITY_ALLY_GLOBAL_CAPS: Record<CityAllyModelId, number> = {
  "g-a": 100,
  "g-b": 50,
  "g-c": 25,
};

/** P4.30 authoritative monster-city footprint, shared by defenses and buildings. */
export const MONSTER_CITY_LAYOUT = {
  wallRings: [
    { ring: 1 as const, extent: 850, health: 1_250, armor: 10, segmentLength: 210 },
    { ring: 2 as const, extent: 1_500, health: 1_850, armor: 16, segmentLength: 220 },
    { ring: 3 as const, extent: 2_250, health: 2_650, armor: 24, segmentLength: 230 },
  ],
  producerDistance: 1_860,
  producerLateral: 265,
  houseDistance: 1_140,
  houseLateral: 330,
} as const;

/** P4.26 placeholder values; presentation stays replaceable. */
export const CITY_ALLY_DEFINITIONS: Record<CityAllyModelId, CityAllyDefinition> = {
  "g-a": { modelId: "g-a", presentationId: "city.ally.g-a", maxHealth: 360, armor: 8, damage: 34, attackIntervalMs: 800, attackRange: 145, moveSpeed: 235, alertRadius: 720, activityRadius: 760 },
  "g-b": { modelId: "g-b", presentationId: "city.ally.g-b", maxHealth: 620, armor: 18, damage: 58, attackIntervalMs: 1_100, attackRange: 115, moveSpeed: 190, alertRadius: 680, activityRadius: 720 },
  "g-c": { modelId: "g-c", presentationId: "city.ally.g-c", maxHealth: 410, armor: 10, damage: 45, attackIntervalMs: 1_000, attackRange: 430, moveSpeed: 205, alertRadius: 820, activityRadius: 780 },
};

const laneVectors = {
  north: { x: 0, y: -1, sideX: 1, sideY: 0 },
  east: { x: 1, y: 0, sideX: 0, sideY: 1 },
  south: { x: 0, y: 1, sideX: -1, sideY: 0 },
  west: { x: -1, y: 0, sideX: 0, sideY: -1 },
} as const;

const producerBlueprints = [
  { suffix: "watchhouse", name: "地精巡逻所", presentationId: "city.building.watchhouse", modelId: "g-a" as const, intervalMs: 15_000, batchSize: 4, lateral: -MONSTER_CITY_LAYOUT.producerLateral, radius: 48, maxHealth: 1_800, armor: 12 },
  { suffix: "muster_hall", name: "兽人集结厅", presentationId: "city.building.muster-hall", modelId: "g-b" as const, intervalMs: 18_000, batchSize: 2, lateral: 0, radius: 54, maxHealth: 2_200, armor: 16 },
  { suffix: "totem_court", name: "萨满图腾庭", presentationId: "city.building.totem-court", modelId: "g-c" as const, intervalMs: 16_000, batchSize: 1, lateral: MONSTER_CITY_LAYOUT.producerLateral, radius: 50, maxHealth: 1_700, armor: 10 },
] as const;

const producerBuildings: CityBuildingDefinition[] = Object.entries(laneVectors).flatMap(
  ([laneId, vector]) => producerBlueprints.map((blueprint) => ({
    id: `${laneId}_${blueprint.suffix}`,
    name: `${laneId === "north" ? "北门" : laneId === "east" ? "东门" : laneId === "south" ? "南门" : "西门"}${blueprint.name}`,
    presentationId: blueprint.presentationId,
    function: "producer" as const,
    offsetX: vector.x * MONSTER_CITY_LAYOUT.producerDistance + vector.sideX * blueprint.lateral,
    offsetY: vector.y * MONSTER_CITY_LAYOUT.producerDistance + vector.sideY * blueprint.lateral,
    radius: blueprint.radius,
    maxHealth: blueprint.maxHealth,
    armor: blueprint.armor,
    production: {
      allyModelId: blueprint.modelId,
      intervalMs: blueprint.intervalMs,
      batchSize: blueprint.batchSize,
    },
  })),
);

const houses: CityBuildingDefinition[] = Object.entries(laneVectors).flatMap(
  ([laneId, vector], laneIndex) => [-1, 1].map((side, index) => ({
    id: `${laneId}_monster_house_${index + 1}`,
    name: `${laneId === "north" ? "北街" : laneId === "east" ? "东街" : laneId === "south" ? "南街" : "西街"}怪物民房`,
    presentationId: index === 0 ? "city.building.storehouse" : "city.building.treasure-masonry",
    function: "none" as const,
    offsetX: vector.x * MONSTER_CITY_LAYOUT.houseDistance + vector.sideX * side * (MONSTER_CITY_LAYOUT.houseLateral + laneIndex * 7),
    offsetY: vector.y * MONSTER_CITY_LAYOUT.houseDistance + vector.sideY * side * (MONSTER_CITY_LAYOUT.houseLateral + laneIndex * 7),
    radius: 44,
    maxHealth: 1_900,
    armor: 12,
  })),
);

/** Four gate routes each own one production group; houses are destructible scenery. */
export const CITY_BUILDING_DEFINITIONS: readonly CityBuildingDefinition[] = [
  ...producerBuildings,
  ...houses,
];

export function getCityAllyDefinition(modelId: CityAllyModelId): CityAllyDefinition {
  return CITY_ALLY_DEFINITIONS[modelId];
}
